import { useEffect, useState } from 'react'
import { apiGet, apiPut } from '../lib/api'
import { useTenantId } from '../context/SessionContext'
import { useToast } from '../context/ToastContext'
import {
  Button,
  Card,
  FormField,
  Input,
  PageHeader,
  Skeleton,
} from '../components/ui'

interface CliSettings {
  refresh_token_ttl_seconds: number
  oidc_cli_prompt: string
  oidc_cli_acr_values: string
}

const MIN_TTL_SECONDS = 900 // 15 minutes
const MAX_TTL_SECONDS = 2592000 // 30 days

export default function AdminCliSettingsPage() {
  const tenantId = useTenantId()
  const { toast } = useToast()
  const [settings, setSettings] = useState<CliSettings | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!tenantId) return
    setLoading(true)
    setError(null)
    apiGet<CliSettings>(`/api/v1/tenants/${tenantId}/cli-settings`)
      .then((d) => setSettings(d))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false))
  }, [tenantId])

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!settings) return
    if (
      settings.refresh_token_ttl_seconds < MIN_TTL_SECONDS ||
      settings.refresh_token_ttl_seconds > MAX_TTL_SECONDS
    ) {
      setError(
        `Session window must be between ${MIN_TTL_SECONDS / 60} minutes and ${MAX_TTL_SECONDS / 86400} days.`,
      )
      return
    }
    setSaving(true)
    setError(null)
    try {
      await apiPut(`/api/v1/tenants/${tenantId}/cli-settings`, settings)
      toast({ title: 'Settings saved', variant: 'success' })
    } catch (e: any) {
      setError(e.message)
      toast({ title: 'Save failed', description: e.message, variant: 'danger' })
    } finally {
      setSaving(false)
    }
  }

  const ttlHours = settings ? (settings.refresh_token_ttl_seconds / 3600).toFixed(2) : ''

  return (
    <div className="max-w-2xl">
      <PageHeader
        title="CLI Login Settings"
        description={
          <>
            Configure <code className="text-foreground">idefy</code> CLI login session behavior for
            this tenant.
          </>
        }
      />

      {error && (
        <div className="mb-4 rounded-lg border border-danger/30 bg-danger-muted px-4 py-3 text-sm text-danger-foreground">
          {error}
        </div>
      )}

      {loading || !settings ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <Card className="p-5">
          <form onSubmit={handleSave} className="space-y-5">
            <FormField
              label="Session window (refresh token TTL)"
              htmlFor="cli-ttl"
              hint={`Allowed range: ${MIN_TTL_SECONDS / 60} minutes – ${MAX_TTL_SECONDS / 86400} days.`}
            >
              <p className="mb-2 text-xs text-muted">
                After a user logs in with <code>idefy login</code> (forcing fresh MFA/passkey
                re-authentication at the identity provider), their CLI session stays active for this
                long without prompting again. Matches AWS STS&apos;s ~1 hour default, tighten this for a
                more security-conscious tenant. Every individual <code>ssh</code> connection still
                mints its own short-lived, single-use ticket regardless of this window.
              </p>
              <div className="flex items-center gap-3">
                <Input
                  id="cli-ttl"
                  type="number"
                  min={MIN_TTL_SECONDS}
                  max={MAX_TTL_SECONDS}
                  value={settings.refresh_token_ttl_seconds}
                  onChange={(e) =>
                    setSettings({ ...settings, refresh_token_ttl_seconds: Number(e.target.value) })
                  }
                  className="w-40"
                />
                <span className="text-xs text-muted">seconds (≈ {ttlHours} hours)</span>
              </div>
            </FormField>

            <FormField label="OIDC prompt" htmlFor="cli-prompt">
              <p className="mb-2 text-xs text-muted">
                Sent as the OIDC <code>prompt</code> parameter on every CLI login. <code>login</code>{' '}
                forces the identity provider to require fresh primary authentication instead of
                silently reusing a browser SSO cookie. This is what makes step-up/MFA/passkey
                enforcement effective. Leave as <code>login</code> unless your IdP requires something
                else.
              </p>
              <Input
                id="cli-prompt"
                value={settings.oidc_cli_prompt}
                onChange={(e) => setSettings({ ...settings, oidc_cli_prompt: e.target.value })}
                placeholder="login"
              />
            </FormField>

            <FormField
              label="OIDC acr_values (optional)"
              htmlFor="cli-acr"
            >
              <p className="mb-2 text-xs text-muted">
                Requests a specific authentication context class from the identity provider (e.g. a
                value your IdP maps to &quot;phishing-resistant&quot; or &quot;passkey required&quot;). Space-separated
                and IdP-specific, leave blank to omit. An incorrect value will surface as a login error
                at the IdP, not a silent downgrade.
              </p>
              <Input
                id="cli-acr"
                value={settings.oidc_cli_acr_values}
                onChange={(e) => setSettings({ ...settings, oidc_cli_acr_values: e.target.value })}
                placeholder="phr"
              />
            </FormField>

            <div className="flex items-center gap-3 pt-1">
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving…' : 'Save Settings'}
              </Button>
              <span className="text-xs text-muted">Tenant-scoped CLI config</span>
            </div>
          </form>
        </Card>
      )}
    </div>
  )
}
